<div class="cds-flex cds-flex-col">
  <!-- Payment Frequency Selector -->
  <div class="cds-mb-4">
    <div
      [cbSelectChoice]="paymentFrequency"
      label="Select payment frequency"
      class="cds-w-full md:cds-w-[300px]"
      colorTheme="default"
      [readonly]="true">
      <p-select
        #paymentFrequency
        [options]="paymentFrequencyOptions()"
        (onChange)="recalculatePremiums($event.value)"
        optionLabel="name"
        optionValue="code"
        placeholder="-"
        inputId="#paymentFrequency"
        [formControl]="paymentFrequencyControl"
        data-testid="premium-breakdown-frequency" />
    </div>
  </div>
  <cb-accordion
    [multiple]="true"
    [value]="['']"
    [variant]="'basic'"
    [padding]="'small'"
    [cuePosition]="'right'"
    [cueType]="'caret'"
    [indent]="true">
    @for (customer of customers(); track customer; let idx = $index) {
      <cb-accordion-panel [attr.data-testid]="`premium-breakdown-accordion-${idx}`">
        <cb-accordion-header>
          <p class="cds-text-neutral-800 cds-my-4 title-bold-4">
            {{ getCustomerFullName(customer) }}
          </p>
        </cb-accordion-header>
        <cb-accordion-content>
          <div class="cds-transition-all cds-duration-300 cds-ease-in-out cds-overflow-hidden">
            <!-- Desktop Design (completely different layout) -->
            <div class="cds-hidden md:cds-block">
              <div class="cds-grid cds-grid-cols-4 cds-gap-y-3 cds-mb-8">
                <p class="body-regular !cds-font-bold cds-text-neutral-800">Coverage Type</p>
                <span></span>
                <p class="body-regular !cds-font-bold cds-text-right cds-text-neutral-800">Sum Insured</p>
                <p class="body-regular !cds-font-bold cds-text-right cds-text-neutral-800">Premium</p>
                @for (coverage of getCoveragesForCustomer(customer); track coverage) {
                  <p class="body-regular">
                    {{ codeVal(coverage?.code ?? '') }}
                  </p>
                  <span></span>
                  <p class="body-regular cds-text-right cds-text-neutral-800">
                    {{
                      coverage?.code !== 'uPremium'
                        ? `$${((coverage?.sumInsured?.amount ?? 0) / 100 | number: '1.2-2')}`
                        : ''
                    }}
                  </p>
                  <p class="body-regular cds-text-right cds-text-neutral-800">
                    ${{ (coverage?.premiumCurrent ?? 0) / 100 | number: '1.2-2' }}
                  </p>
                }
                <p class="body-regular !cds-font-bold cds-text-neutral-800">Total</p>
                <span></span>
                <span></span>
                <p class="body-regular cds-text-right">${{ getTotalPremium(customer) / 100 | number: '1.2-2' }}</p>
              </div>
            </div>
            <!-- Mobile Design (completely different layout) -->
            <div class="cds-block md:cds-hidden">
              @for (coverage of getCoveragesForCustomer(customer); track coverage; let idx = $index) {
                <div class="cds-flex cds-flex-col">
                  <!-- Coverage -->
                  <p class="body-regular !cds-font-bold cds-text-neutral-800">Coverage Type</p>
                  <p class="body-regular cds-mb-2 cds-text-neutral-800">
                    {{ codeVal(coverage?.code!) }}
                  </p>
                  <div class="cds-grid cds-grid-cols-2 cds-mb-4">
                    <p class="body-regular !cds-font-bold cds-text-neutral-800">Sum Insured</p>
                    <p class="body-regular cds-text-right cds-text-neutral-800">
                      {{
                        coverage?.code !== 'uPremium'
                          ? `$${((coverage?.sumInsured?.amount ?? 0) / 100 | number: '1.2-2')}`
                          : ''
                      }}
                    </p>
                    <p class="body-regular !cds-font-bold cds-text-neutral-800">Premium</p>
                    <p class="body-regular cds-text-right cds-mb-3 cds-text-neutral-800">
                      ${{ (coverage?.premiumCurrent ?? 0) / 100 | number: '1.2-2' }}
                    </p>
                  </div>
                  @if (idx === getCoveragesForCustomer(customer).length - 1) {
                    <div class="cds-grid cds-grid-cols-2">
                      <p class="body-regular !cds-font-bold cds-text-neutral-800">Total</p>
                      <p class="body-regular cds-text-right cds-text-neutral-800">
                        ${{ getTotalPremium(customer) / 100 | number: '1.2-2' }}
                      </p>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        </cb-accordion-content>
      </cb-accordion-panel>
    }
  </cb-accordion>

  <!-- Policy Fee and Premium -->
  <!-- Desktop Design (completely different layout) -->
  <div class="cds-hidden md:cds-block">
    <div class="cds-border-t-1 cds-border-[#e0e0e0]">
      <div class="cds-pt-6 cds-grid cds-grid-cols-2 cds-gap-y-1">
        <p class="cds-text-neutral-800 body-regular !cds-font-bold cds-text-[20px]">Policy Fee</p>
        <p
          class="cds-text-neutral-800 body-regular !cds-font-bold cds-text-right cds-text-[20px]"
          data-testid="premium-breakdown-fee">
          ${{ policy().fee?.amount! / 100 | number: '1.2-2' }}
        </p>
        <p class="title-bold-4 cds-text-purple-500">
          Premium per {{ paymentFrequencyLabel() }}
        </p>
        <p
          class="cds-text-right title-bold-4 cds-text-purple-500"
          data-testid="premium-breakdown-premium">
          ${{ policy().premium?.amount! / 100 | number: '1.2-2' }}
        </p>
      </div>
    </div>
  </div>
  <!-- Mobile Design (completely different layout) -->
  <div class="cds-block md:cds-hidden">
    <div class="cds-border-t-1 cds-border-[#e0e0e0]">
      <div class="cds-pt-4 cds-grid cds-grid-cols-2 cds-mb-4 cds-gap-y-1">
        <p class="body-regular !cds-font-bold cds-text-neutral-800">Policy Fee</p>
        <p class="body-regular !cds-font-bold cds-text-right cds-text-neutral-800" data-testid="premium-breakdown-fee">
          ${{ policy().fee?.amount! / 100 | number: '1.2-2' }}
        </p>
        <p class="title-bold-4 cds-text-purple-500">Premium per {{ paymentFrequencyLabel() }}</p>
        <p class="cds-text-right title-bold-4 cds-text-purple-500" data-testid="premium-breakdown-premium">
          ${{ policy().premium?.amount! / 100 | number: '1.2-2' }}
        </p>
      </div>
    </div>
  </div>
</div>
